import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { StatusColorsKeys, status_colors } from "../../services/shipping";
import { useError } from "../../contexts/ErrorContext";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";
import RefetchingTimestamp from "../../components/UI/RefetchingTimestamp";
import Plot from "react-plotly.js";
import { ShippingStatusSummary_T } from "../../types/Shipping";
interface ShippingStatusSummaryChartProps {
  dateStart: string;
  dateEnd: string;
  statusSummary?: ShippingStatusSummary_T[] | null;
  isLoading?: boolean;
  showUpdateTimestamp?: boolean;
  onUpdateTimestamp?: (timestamp: number | null) => void;
}

const ShippingStatusSummaryChart: React.FC<ShippingStatusSummaryChartProps> = ({
  dateStart,
  dateEnd,
  statusSummary: propsStatusSummary,
  isLoading: propsIsLoading,
  showUpdateTimestamp = false,
  onUpdateTimestamp,
}) => {
  const { showError } = useError();
  const [chartData, setChartData] = useState<any[]>([]);

  const {
    statusSummary: localStatusSummary,
    detailsError,
    dataUpdatedAt,
    isLoading: localIsLoading,
    shippingDetailsRefetching: isRefetching,
  } = useShippingDataCoordinator(dateStart, dateEnd);

  const statusSummary = propsStatusSummary ?? localStatusSummary;
  const isLoading = propsIsLoading ?? localIsLoading;

  useEffect(() => {
    if (onUpdateTimestamp && dataUpdatedAt) {
      onUpdateTimestamp(dataUpdatedAt);
    }
  }, [dataUpdatedAt, onUpdateTimestamp]);

  useEffect(() => {
    if (statusSummary && statusSummary.length > 0) {
      const transformedData = {
        name: "Status",
      } as { [key: string]: number | string };

      Object.keys(status_colors).forEach((key) => {
        const statusItem = statusSummary.find((item) => item.status === key);
        transformedData[key] = statusItem ? statusItem.count : 0;
      });

      setChartData([transformedData]);
    }
  }, [statusSummary]);

  useEffect(() => {
    if (detailsError) {
      showError(
        `Failed to get shipping details for dates: ${dateStart} - ${dateEnd}`
      );
    }
  }, [detailsError, showError, dateStart, dateEnd]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Plot
            config={{
              displaylogo: false,
              modeBarButtonsToRemove: ["select2d", "lasso2d"],
              responsive: true,
            }}
            data={Object.keys(status_colors).map((status) => ({
              type: "bar",
              x: chartData.map((item) => item.name),
              y: chartData.map((item) => item[status as StatusColorsKeys]),
              name: status,
              marker: { color: status_colors[status as StatusColorsKeys] },
              hovertemplate: `%{x}<br>${status}: %{y} (%{customdata}%)<extra></extra>`,
              customdata: chartData.map((item) => {
                const total = Object.keys(status_colors)
                  .map((key) => item[key as StatusColorsKeys])
                  .reduce((sum, value) => sum + value, 0);
                const percentage =
                  total > 0
                    ? (
                        (item[status as StatusColorsKeys] / total) *
                        100
                      ).toFixed(2)
                    : "0.00";
                return percentage;
              }),
              yaxis: "y1",
            }))}
            layout={{
              barmode: "stack",
              margin: { t: 0, r: 0, l: 60, b: 0 },
              showlegend: false,
              xaxis: {
                type: "category",
                visible: false,
              },
              yaxis: {
                title: { text: "Count" },
                type: "linear",
                showticklabels: true,
                range: [
                  0,
                  Math.max(
                    ...chartData.map((item) =>
                      Object.keys(status_colors)
                        .map((key) => item[key as StatusColorsKeys])
                        .reduce((sum, value) => sum + value, 0)
                    )
                  ),
                ], // Set range for total count of data records
              },
              yaxis2: {
                title: { text: "Percentage" },
                type: "linear",
                overlaying: "y",
                side: "right",
                tickformat: ".0%",
                showticklabels: true,
                range: [0, 1], // Set range for percentage (0 to 100%)
              },
              autosize: true, // Add this instead
              paper_bgcolor: "transparent",
              plot_bgcolor: "transparent",
              hoverlabel: {
                bgcolor: "#FFF",
                font: { color: "#264653" },
                align: "left",
              },
              font: {
                family: '"Noto Sans", serif',
                size: 14,
              },
            }}
            style={{ width: "100%", height: "100%" }}
          />

          {showUpdateTimestamp && dataUpdatedAt && (
            <Box sx={{ mt: 2, width: "100%", textAlign: "center" }}>
              <RefetchingTimestamp
                isRefetching={isRefetching}
                updatedAtTimestamp={dataUpdatedAt}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ShippingStatusSummaryChart;
